/**
 * TestAvailabilityEditor — admin form section for J-X22 exam-window edits.
 *
 * Used inside `pages/admin/tests/edit.tsx`. Wraps the three availability
 * columns (`availability_starts_at`, `availability_ends_at`,
 * `availability_closed_reason`) and posts to the dedicated admin endpoint
 * `PATCH /api/admin/tests/:id/availability` (separate from the regular
 * PATCH /api/tests/:id so the audit trail can grow per-column controls
 * later).
 *
 * Inv 15: Button / Card / Heading / Input from `@/components/ui` barrel.
 */

import { useState, type ReactElement } from 'react';
import { Button, Card, Heading, Input } from '@/components/ui';
import { adminApi } from '../../state/admin/api';
import { AvailabilityChip } from '../availability/AvailabilityChip';

export interface TestAvailabilityEditorProps {
  testId: string;
  initialStartsAt: string | null;
  initialEndsAt: string | null;
  initialClosedReason: string | null;
  onSaved?: (next: {
    availability_starts_at: string | null;
    availability_ends_at: string | null;
    availability_closed_reason: string | null;
  }) => void;
}

/** Convert ISO -> input[type=datetime-local] value (truncate to minutes). */
function isoToLocalInput(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // datetime-local takes 'YYYY-MM-DDTHH:MM' in local time.
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(value: string): string | null {
  if (value === '') return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function TestAvailabilityEditor(
  props: TestAvailabilityEditorProps,
): ReactElement {
  const [startsAt, setStartsAt] = useState<string>(
    isoToLocalInput(props.initialStartsAt),
  );
  const [endsAt, setEndsAt] = useState<string>(
    isoToLocalInput(props.initialEndsAt),
  );
  const [closedReason, setClosedReason] = useState<string>(
    props.initialClosedReason ?? '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleSave = (): void => {
    void (async () => {
      setSaving(true);
      setError(null);
      setInfo(null);
      const body = {
        availability_starts_at: localInputToIso(startsAt),
        availability_ends_at: localInputToIso(endsAt),
        availability_closed_reason: closedReason.trim() === '' ? null : closedReason.trim(),
      };
      const env = await adminApi.patchTestAvailability(props.testId, body);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        setSaving(false);
        return;
      }
      setInfo('Availability saved.');
      setSaving(false);
      props.onSaved?.(env.data);
    })();
  };

  const previewInput = {
    availability_starts_at: localInputToIso(startsAt),
    availability_ends_at: localInputToIso(endsAt),
    availability_closed_reason: closedReason.trim() === '' ? null : closedReason.trim(),
  };

  return (
    <Card variant="default" className="mt-6 p-4">
      <Heading level="h3" className="text-h3 text-ink">
        Availability window
      </Heading>
      <p className="mt-1 font-sans text-small text-muted">
        Leave both empty to keep the test always-open. Add a start to schedule
        access; add an end to close the window. Closed reason appears on
        catalogue cards once the window has lapsed.
      </p>

      <div
        role="note"
        aria-label="How candidates see this availability window"
        className="mt-3 rounded-md border border-accent-l bg-accent-l p-3"
      >
        <p className="font-mono text-eyebrow uppercase tracking-wide text-accent">
          How candidates see this
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5 font-sans text-small text-ink-2">
          <li>
            <span className="font-medium text-ink">Before the start:</span>{' '}
            the catalogue card and test page show &ldquo;Available from
            &lt;date&gt;&rdquo; and the Begin Test button is disabled.
          </li>
          <li>
            <span className="font-medium text-ink">Inside the window:</span>{' '}
            candidates can take the test; a banner warns them when the close
            time is approaching.
          </li>
          <li>
            <span className="font-medium text-ink">After the end:</span>{' '}
            the test reads as closed, the Begin Test button is disabled, and
            your closed reason (if set) is shown so candidates know what to do
            next.
          </li>
        </ul>
        <p className="mt-2 font-sans text-small text-muted">
          All dates display in each candidate&rsquo;s own timezone. Retake
          cooldowns are separate and shown automatically on the candidate
          dashboard.
        </p>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="font-mono text-eyebrow uppercase tracking-wide text-muted">
            Available from
          </span>
          <Input
            type="datetime-local"
            value={startsAt}
            onChange={(e) => {
              setStartsAt(e.target.value);
            }}
            disabled={saving}
            aria-label="Availability start"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono text-eyebrow uppercase tracking-wide text-muted">
            Available until
          </span>
          <Input
            type="datetime-local"
            value={endsAt}
            onChange={(e) => {
              setEndsAt(e.target.value);
            }}
            disabled={saving}
            aria-label="Availability end"
          />
        </label>
        <label className="flex flex-col gap-1 md:col-span-2">
          <span className="font-mono text-eyebrow uppercase tracking-wide text-muted">
            Closed reason (shown to candidates once window has lapsed)
          </span>
          <Input
            type="text"
            value={closedReason}
            onChange={(e) => {
              setClosedReason(e.target.value);
            }}
            disabled={saving}
            placeholder="e.g. see T-window v2 for the replacement"
            aria-label="Closed reason"
          />
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <span className="font-sans text-small text-muted">Preview:</span>
        <AvailabilityChip input={previewInput} hideAlwaysOpen={false} />
      </div>

      {error !== null ? (
        <p role="alert" className="mt-3 font-sans text-small text-error">
          {error}
        </p>
      ) : null}
      {info !== null ? (
        <p role="status" className="mt-3 font-sans text-small text-ink-2">
          {info}
        </p>
      ) : null}

      <div className="mt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save availability'}
        </Button>
      </div>
    </Card>
  );
}
